(ns practitest.frontend.components.charts
  (:require
    ["chart.js" :refer [Chart ArcElement Tooltip Legend GaugeController Filler]]
    ["react-chartjs-2" :refer [Pie Doughnut Line]]
    [practitest.frontend.components.icons :as icons]))

(.register Chart ArcElement Tooltip Legend Filler)

(defn pie [{:keys [labels ds-label ds-data ds-colors display-legends?]
            :or {display-legends? true}}]
  [:> Pie
   {:data    {:labels   labels
              :datasets [{:label           ds-label
                          :data            ds-data
                          :backgroundColor ds-colors}]}
    :options {:plugins {:legend {:display display-legends?}}}}])

(defn generate-percentage-labels [^js chart]
  (->> (.-labels (.-data chart))
       (map-indexed (fn [i label]
                      (let [meta       (.getDatasetMeta chart 0)
                            dataset    (aget (.-data chart) "datasets" 0 "data")
                            current    (aget dataset i)
                            total      (reduce + dataset)
                            percentage (str (js/Math.round (* 100 (/ current total))) "%")
                            label-options (.-options (aget (.-data meta) i))]
                        #js {:text        (str label ": " percentage)
                             :fillStyle (when label-options (.-backgroundColor label-options))
                             :strokeStyle "#fff"
                             :lineWidth   2
                             :hidden      (js/isNaN current)
                             :index       i})))
       (into-array)
       (clj->js)))

(defn doughnut [{:keys [labels ds-label ds-data ds-colors]}]
  [:> Doughnut
   {:data    {:labels   labels
              :datasets [{:label           ds-label
                          :data            ds-data
                          :backgroundColor ds-colors}]}
    :options {:plugins {:legend {:display false}}}}])

(defn mux-doughnut-tooltip-cb [^js context]
  (let [label (.-label context)
        data (.-data (.-dataset context))
        current (aget data (.-dataIndex context))
        total (reduce + data)
        percentage (str (if (zero? total)
                          0
                          (js/Math.round (* 100 (/ current total))))
                        "%")]
    (str label ": " (.-formattedValue context) " (" percentage ")")))

(defn mux-doughnut [{:keys [labels datasets cutout]}]
  [:> Doughnut
   {:data    {:labels   labels
              :datasets (mapv (fn [{:keys [label data bg-colors]}]
                                {:label label
                                 :data data
                                 :backgroundColor bg-colors})
                              datasets)}
    :options {:cutout cutout
              :plugins {:legend {:display false
                                 :position "chartArea"
                                 :labels {:generateLabels generate-percentage-labels}}
                        :tooltip {:backgroundColor "#fff"
                                  :titleColor "#000"
                                  :bodyColor "#000"
                                  :footerColor "#000"
                                  :callbacks {:label mux-doughnut-tooltip-cb}}}
              :maintainAspectRatio false}}])

(defn line-chart []
  [:> Line
   {:data    {:labels ["21/08" "22/08" "23/08" "24/08" "25/08"]
              :datasets [{:label "CANCELLED"
                          :data  [40 40 40 41 42]
                          :fill true
                          :borderColor "#FFCD29"
                          :backgroundColor "#FFCD2950"}
                         {:label "FAILED"
                          :data  [28 20 22 23 20]
                          :fill true
                          :borderColor "#D6242A"
                          :backgroundColor "#D6242A50"}]}
    :options {:plugins {:legend {:display false}}}}])

(defn make-area-chart-tooltip-label-cb [raw-datasets]
  (fn [^js context]
    (let [dataset-index (.-datasetIndex context)
          index (.-dataIndex context)
          label' (.-label (.-dataset context))
          raw-value (get-in raw-datasets [dataset-index :data index])]
      (str label' ": " raw-value))))

(defn line-area-chart [{:keys [labels datasets]}]
  (let [aggregated-datasets (reduce (fn [acc dataset]
                                      (let [prev-data (if (empty? acc)
                                                        (repeat (count (:data dataset)) 0)
                                                        (:data (last acc)))
                                            new-data (map + prev-data (:data dataset))]
                                        (conj acc (assoc dataset
                                                         :data new-data
                                                         :fill (if (empty? acc)
                                                                 true
                                                                 (dec (count acc)))))))
                                    [] datasets)]
    [:> Line
     {:data {:labels labels
             :datasets (mapv (fn [{:keys [label data fill color bg-color]}]
                               {:label label
                                :data data
                                :pointRadius 1
                                :borderWidth 1
                                :fill fill
                                :borderColor color
                                :backgroundColor bg-color})
                             aggregated-datasets)}
      :options {
                ;; Doesn't resize well on small heights in instances dashboards without this
                :maintainAspectRatio false
                :plugins
                {:legend {:display false}
                 :tooltip {:backgroundColor "#fff"
                           :titleColor "#000"
                           :bodyColor "#000"
                           :footerColor "#000"
                           :callbacks {:label (make-area-chart-tooltip-label-cb datasets)}}}}}]))

(defn percentage-gauge [{:keys [percentage title-fn subtitle-fn]}]
  (let [percentage (-> percentage (* 100) (.toFixed 2))
        color      (condp > percentage
                     30 "rgba(87, 134, 78, 0.8)"
                     80 "rgba(250, 220, 86, 0.8)"
                     "rgba(172, 81, 76, 0.8)")]
    [:> Doughnut
     {:data    {:labels   []
                :datasets [{:data            [percentage (- 100 percentage)]
                            :backgroundColor [color "rgba(224, 224, 224, 0.9)"]}]}
      :options {:rotation      270
                :circumference 180
                :cutout        "80%"
                :plugins       {:legend   {:display false}
                                :tooltip  {:enabled false}
                                :title    {:display  true
                                           :text     (title-fn percentage)
                                           :position "bottom"
                                           :font {:weight "bold"}
                                           :padding {:top -40}}
                                :subtitle {:display  true
                                           :text     (subtitle-fn percentage)
                                           :position "bottom"
                                           :font {:weight "bold"}
                                           :padding {:top -20}}}}}]))

;; https://github.com/chartjs/Chart.js/issues/2874
;; https://www.youtube.com/watch?v=VF6jd2Fv4bs
(defn draw-needle [canvas radius radian-angle percentage]
  (let [ctx (.getContext canvas "2d")
        offsetTop (.-offsetTop canvas)
        cw (.-offsetWidth canvas)
        ch (.-offsetHeight canvas)
        cx (/ cw 2)
        cy (- ch (/ ch 6))]
    ;; needle
    ; a new starting point at the center
    (.translate ctx cx cy)
    ; position the line
    (.rotate ctx radian-angle)
    ; start the draw
    (.beginPath ctx)
    ; the drawing
    (.moveTo ctx 0 -5)
    ; indicate where the line is going. line length
    (.lineTo ctx (/ radius 2) 0)

    ; make it tick at the bottom
    (.lineTo ctx 0 5)
    ; set line color
    (aset ctx "fillStyle" "rgba(95, 95, 95, 0.8)")
    (.fill ctx)

    ; needle dot
    (.rotate ctx (- radian-angle))
    (.translate ctx (- cx) (- cy))
    (.beginPath ctx)
    (.arc ctx cx cy 7 0 (* 2 Math/PI))
    (.fill ctx)
    (.restore ctx)

    (set! (.-font ctx) "20px Lucida Grande")
    (set! (.-fillStyle ctx) "rgb(0, 0, 0)")
    (.fillText ctx (str percentage "%") (- cx 30) (+ cy 45))
    (.restore ctx)))

(defn percentage-gauge-with-needle [{:keys [percentage title areas filter]}]
  (let [canvas-ref (atom nil)]
    (fn [{:keys [percentage title areas filter]}]
      (let [percentage (-> percentage (* 100) (.toFixed 2))
            first (first areas)
            second (second areas)
            last (last areas)]

        [:> Doughnut
         {:data    {:labels   []
                    :datasets [{:data            [first second last]
                                :backgroundColor ["rgb(195, 95, 92)" ;red
                                                  "rgb(255,255,102)" ;yellow
                                                  "rgba(95, 161, 96)"] ;green
                                :cutout          "45%"
                                }]}
          :ref     (fn [node]
                     (reset! canvas-ref (.-canvas node)))
          :options {
                    :rotation      270
                    :circumference 180
                    :cutout        "80%"
                    :plugins       {:legend   {:display false}
                                    :tooltip  {:enabled false}
                                    :title    {:display   true
                                               :text      title
                                               :position  "top"
                                               :color     "#3E576F"
                                               :fill      "#3E576F"
                                               :font      {:family "Lucida Grande"
                                                           :weight "normal"
                                                           :size   "16px"}
                                               :padding   {:top 10}
                                               :className "title"}
                                    :subtitle {:display  true
                                               :text     (str filter)
                                               :position "top"
                                               :color    "#6D869F"
                                               :fill     "#6D869F"
                                               :font     {:family "Lucida Grande"
                                                          :weight "normal"
                                                          :size   "12px"}
                                               :padding  {:top 2}}
                                    }
                    :animation     {:onComplete (fn [chart]
                                                  (let [radius 250
                                                        radian-angle (+ Math/PI (* (/ 1 100) percentage Math/PI))]
                                                    (when @canvas-ref
                                                      (draw-needle @canvas-ref radius radian-angle percentage))))}
                    }}]))))
